import {
  DEFAULT_PERSO_MARGIN_MIN,
  MAX_PERSO_MARGIN_MIN,
  MIN_PERSO_MARGIN_MIN,
  type Settings,
} from '@ergo/shared';
import { type FormEvent, useEffect, useState } from 'react';
import { api, errorMessage } from '../../api';
import { useResource } from '../../hooks';
import { formatSyncMoment } from '../../perso';

/**
 * Les deux sens, sans jamais donner à Google le droit d'écrire chez elle : son
 * agenda perso entre par son adresse iCal privée, ses séances en ressortent par un
 * flux anonyme. Rien d'identifiant ne quitte le serveur — c'est une contrainte
 * légale, pas une préférence.
 */
export function GoogleAgenda() {
  const settings = useResource(() => api.settings.get(), []);

  return (
    <section className="panel">
      <h2 className="panel__title">Google Agenda</h2>
      <AgendaPerso settings={settings} />
      <FluxSeances settings={settings} />
    </section>
  );
}

type SettingsResource = ReturnType<typeof useResource<Settings>>;

/** Sens 1 : ce que Google sait de sa vie, posé en gris derrière ses séances. */
function AgendaPerso({ settings }: { settings: SettingsResource }) {
  const [url, setUrl] = useState('');
  const [marginMin, setMarginMin] = useState(DEFAULT_PERSO_MARGIN_MIN);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    if (!settings.data) return;
    setMarginMin(settings.data.persoMarginMin);
    setUrl('');
  }, [settings.data]);

  const configured = settings.data?.googleIcalConfigured ?? false;
  const fetchedAt = settings.data?.googleIcalFetchedAt ?? null;
  const syncError = settings.data?.googleIcalError ?? null;

  async function run(action: () => Promise<string>) {
    setPending(true);
    setError(null);
    setNotice(null);
    try {
      setNotice(await action());
      settings.reload();
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setPending(false);
    }
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    await run(async () => {
      await api.settings.update({
        persoMarginMin: marginMin,
        // Champ laissé vide : la marge s'enregistre, l'adresse déjà là ne bouge pas.
        ...(url.trim() === '' ? {} : { googleIcalUrl: url }),
      });
      setUrl('');
      return 'Enregistré.';
    });
  }

  return (
    <>
      <h3 className="panel__title panel__title--sub">Mes rendez-vous perso dans l’app</h3>
      <p className="hint">
        Dans Google Agenda : Paramètres de l’agenda → Intégrer l’agenda → « Adresse secrète au
        format iCal ». Collée ici, l’app y lit vos rendez-vous et les pose en gris derrière vos
        séances, pour que vous ne planifiez pas une séance dessus.
      </p>

      <form className="form" onSubmit={onSubmit}>
        <label className="field">
          <span className="field__label">Adresse secrète iCal</span>
          <input
            className="input"
            type="password"
            autoComplete="off"
            placeholder={
              configured
                ? '•••••••••••••••••••• (enregistrée)'
                : 'https://calendar.google.com/calendar/ical/…/private-…/basic.ics'
            }
            value={url}
            onChange={(event) => setUrl(event.target.value)}
          />
          <span className="hint">
            Cette adresse ouvre tout votre agenda : elle est enregistrée sur le serveur et n’en
            ressort jamais. Laisser vide conserve celle déjà enregistrée.
          </span>
        </label>

        {configured && (
          <p className={syncError ? 'note-sun' : 'hint'}>
            {syncError
              ? `Synchro impossible : ${syncError}.`
              : fetchedAt
                ? `Configuré · dernière synchro ${formatSyncMoment(fetchedAt)}`
                : 'Configuré · première synchro en cours…'}
          </p>
        )}

        <label className="field">
          <span className="field__label">Marge autour d’un rendez-vous perso</span>
          <span className="suffixed">
            <input
              className="input"
              type="number"
              min={MIN_PERSO_MARGIN_MIN}
              max={MAX_PERSO_MARGIN_MIN}
              step={5}
              value={marginMin}
              onChange={(event) => setMarginMin(Number(event.target.value))}
              required
            />
            <span className="suffixed__unit">min</span>
          </span>
          <span className="hint">
            L’app prévient quand une séance tombe à moins de ça d’un rendez-vous perso.
          </span>
        </label>

        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}

        <div className="form__actions">
          <button className="button button--primary" type="submit" disabled={pending}>
            {pending ? 'Enregistrement…' : 'Enregistrer'}
          </button>

          {configured && (
            <button
              type="button"
              className="button"
              disabled={pending}
              onClick={() =>
                void run(async () => {
                  await api.settings.update({ googleIcalUrl: null });
                  return 'Adresse retirée.';
                })
              }
            >
              Retirer
            </button>
          )}

          {notice && <span className="muted">{notice}</span>}
        </div>
      </form>
    </>
  );
}

/** Sens 2 : le flux anonyme auquel elle abonne Google, pour voir ses créneaux pris. */
function FluxSeances({ settings }: { settings: SettingsResource }) {
  const [includesTravel, setIncludesTravel] = useState(true);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    if (settings.data) setIncludesTravel(settings.data.feedIncludesTravel);
  }, [settings.data]);

  const feedUrl = settings.data?.seancesFeedUrl ?? '';

  async function run(action: () => Promise<string>) {
    setPending(true);
    setError(null);
    setNotice(null);
    try {
      setNotice(await action());
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setPending(false);
    }
  }

  return (
    <>
      <h3 className="panel__title panel__title--sub">Mes séances dans Google Agenda</h3>
      <p className="hint">
        Dans Google Agenda : « Autres agendas » → « + » → « À partir de l’URL », puis collez cette
        adresse. Google la relit toutes les quelques heures : une séance ajoutée aujourd’hui met un
        moment à y apparaître.
      </p>

      <div className="form">
        <label className="field">
          <span className="field__label">Adresse du flux</span>
          <span className="field__row">
            <input className="input" readOnly value={feedUrl} onFocus={selectAll} />
            <button
              type="button"
              className="button"
              disabled={pending || feedUrl === ''}
              onClick={() =>
                void run(async () => {
                  await navigator.clipboard.writeText(feedUrl);
                  return 'Adresse copiée.';
                })
              }
            >
              Copier
            </button>
          </span>
          <span className="hint">
            Le flux ne contient que le mot « Séance » et des heures : aucun nom, aucun lieu, rien
            qui puisse désigner un patient.
          </span>
        </label>

        <div className="field">
          <label className="checkbox">
            <input
              type="checkbox"
              checked={includesTravel}
              disabled={pending}
              onChange={(event) => {
                const next = event.target.checked;
                setIncludesTravel(next);
                void run(async () => {
                  await api.settings.update({ feedIncludesTravel: next });
                  return 'Enregistré.';
                });
              }}
            />
            <span>Inclure le temps de trajet à vélo</span>
          </label>
          <span className="hint">
            La case commence alors à l’heure du départ, quand le trajet est déjà connu.
          </span>
        </div>

        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}

        <div className="form__actions">
          <button
            type="button"
            className="button"
            disabled={pending}
            onClick={() => {
              if (
                !window.confirm(
                  'Régénérer l’adresse ? L’ancienne cessera de fonctionner : il faudra réabonner Google Agenda.',
                )
              ) {
                return;
              }
              void run(async () => {
                await api.settings.regenerateFeed();
                settings.reload();
                return 'Nouvelle adresse : réabonnez Google Agenda.';
              });
            }}
          >
            Régénérer l’adresse
          </button>
          {notice && <span className="muted">{notice}</span>}
        </div>
      </div>
    </>
  );
}

function selectAll(event: { target: EventTarget & HTMLInputElement }): void {
  event.target.select();
}
